import { useEffect, useState } from "react";

/**
 * 필수·배제 카드(2026-09-25 사용자 요청·시안 확인) — 지도에서 고른 세번이 되려면 맞아야 하는 조건(필수)과, 하나라도 해당하면
 * 다른 곳으로 가는 조건(배제 → 도착 세번)을 보여 준다. 관세사 이력 비율이 높은 다른 갈래로 바로 옮겨 볼 수 있다.
 *
 * 조건은 서버가 관세율표 원문을 옮긴 검수 트리(조건식·소호 원문·국내 결정표)에서만 읽는다. 상태는 사용자가 답한 사실로만
 * 매기고(✓ 맞음 · ✕ 어긋남 · ? 확인 필요), 카드는 판정·저장을 하지 않는다. 답은 지도 위의 질문에서 한다.
 */
const BLUE = "#1d4ed8";
const LINE = "var(--audit-border, #d8dee6)";
const MUTED = "var(--audit-text-muted, #5b6573)";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const LEVEL_LABEL = { chapter: "류", hs4: "호", hs6: "소호", kr10: "국내 10자리" };

export function formatCode(code) {
  const d = String(code || "").replace(/\D/g, "");
  if (d.length === 2) return `제${d}류`;
  if (d.length === 4) return `제${d}호`;
  if (d.length === 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  if (d.length === 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
  return d;
}

function mark(state) {
  if (state === "true") return { icon: "✓", color: BLUE, label: "맞음" };
  if (state === "false") return { icon: "✕", color: "#475569", label: "어긋남" };
  if (state === "invalid") return { icon: "✕", color: "#475569", label: "값 범위 밖" };
  if (state === "not_applicable") return { icon: "–", color: MUTED, label: "해당 없음" };
  return { icon: "?", color: MUTED, label: "확인 필요" };
}

function answerText(row) {
  if (row.answer === undefined || row.answer === null) return "";
  const unit = row.expected?.unit ? String(row.expected.unit).trim() : "";
  return `답: ${row.answer}${unit && typeof row.answer === "number" ? unit : ""}`;
}

/**
 * @param code        고른 마디(호·소호·국내 10자리)
 * @param answersKey  답한 사실이 바뀌면 다시 불러오도록(표시용 키)
 * @param fetchConditions (code) => Promise<서버 응답>
 * @param labelFor    (code) => 한글 이름(지도 마디에서) | ""
 * @param historyFor  (code) => { share } | null — 관세사 이력 비율
 * @param alternatives [{ code, share }] 이력 비율이 높은 다른 갈래
 * @param canSelect   (code) => 지도에 그 마디가 있어 고를 수 있는가
 * @param onSelect    (code) => 지도에서 그 마디를 고른다
 */
export default function TargetConditionsCard({ code, answersKey = "", fetchConditions, labelFor = () => "", historyFor = () => null, alternatives = [], canSelect = () => false, onSelect = null }) {
  const [state, setState] = useState({ code: null, loading: false, data: null, error: false });
  useEffect(() => {
    if (!code || !fetchConditions) return undefined;
    let alive = true;
    setState({ code, loading: true, data: null, error: false });
    Promise.resolve(fetchConditions(code))
      .then((data) => { if (alive) setState({ code, loading: false, data, error: !data }); })
      .catch(() => { if (alive) setState({ code, loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, [code, answersKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!code || !fetchConditions) return null;
  const data = state.code === code ? state.data : null;
  const hist = historyFor(code);
  const required = Array.isArray(data?.required) ? data.required : [];
  const exclusions = Array.isArray(data?.exclusions) ? data.exclusions : [];
  const known = required.filter((r) => r.kind !== "legal_text" && r.state === "true").length;
  const open = required.filter((r) => r.kind !== "legal_text" && (r.state === "unknown" || !r.state)).length;
  const off = required.filter((r) => r.state === "false" || r.state === "invalid").length;
  const textOf = (r) => (r.kind === "legal_text" ? (labelFor(r.code) || r.text) : r.text);
  const destButton = (d) => {
    const h = historyFor(d);
    const label = labelFor(d);
    const content = <>{formatCode(d)}{h && h.share > 0 ? <span style={{ color: MUTED, fontSize: 11 }}> · 이력 {Math.round(h.share)}%</span> : null}</>;
    return onSelect && canSelect(d)
      ? <button key={d} type="button" title={label || undefined} onClick={() => onSelect(d)} style={{ border: 0, background: "none", padding: 0, color: BLUE, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, textDecoration: "underline", textUnderlineOffset: 2 }}>{content}</button>
      : <span key={d} title={label || undefined} style={{ fontSize: 12.5 }}>{content}</span>;
  };
  return (
    <section data-testid="target-conditions" data-code={code} aria-label="필수·배제 조건" style={{ marginTop: 10, border: `1px solid ${LINE}`, borderRadius: 8, background: "var(--audit-elev, #fff)", padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, color: BLUE, fontFamily: MONO }}>{formatCode(code)}</b>
        <b style={{ fontSize: 13 }}>이(가) 되려면</b>
        {hist && hist.share > 0 && <span style={{ fontSize: 11.5, padding: "1px 8px", borderRadius: 999, background: "#eef3fd", color: BLUE, fontWeight: 600 }}>관세사 이력 {Math.round(hist.share)}%</span>}
        <span style={{ fontSize: 11.5, padding: "1px 8px", borderRadius: 999, background: "#eef0f3", color: "#3b4452" }}>조건부 · 확정 아님</span>
        {data?.supported && <span data-testid="target-conditions-counts" style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>맞음 {known} · 확인 필요 {open}{off ? ` · 어긋남 ${off}` : ""}</span>}
      </div>
      {alternatives.length > 0 && (
        <div data-testid="target-conditions-alternatives" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, fontSize: 12 }}>
          <span style={{ color: MUTED }}>다른 갈래 · 관세사 이력</span>
          {alternatives.map((a) => (
            <button key={a.code} type="button" onClick={() => onSelect && onSelect(a.code)} style={{ border: `1px solid ${a.code === code ? BLUE : LINE}`, background: a.code === code ? "#eef3fd" : "transparent", color: a.code === code ? BLUE : "inherit", borderRadius: 999, padding: "1px 9px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>
              <span style={{ fontFamily: MONO }}>{formatCode(a.code)}</span> {Math.round(a.share)}%
            </button>
          ))}
        </div>
      )}
      {state.loading && <div role="status" style={{ marginTop: 8, fontSize: 12, color: MUTED }}>조건을 불러오는 중…</div>}
      {!state.loading && state.error && <div role="alert" style={{ marginTop: 8, fontSize: 12, color: MUTED }}>조건을 불러오지 못했습니다.</div>}
      {!state.loading && data && data.supported === false && (
        <div data-testid="target-conditions-unsupported" style={{ marginTop: 8, fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
          {data.reason === "code_not_in_domestic_table" || data.reason === "code_not_in_tariff" ? "이 10자리는 현행 관세율표에 없는 세번입니다." : "이 류는 조건을 한곳에 모은 공통 트리 형식이 아니라 아직 필수·배제 조건을 보여 주지 못합니다. 위 지도의 질문과 \"이 세번이 되려면\" 목록을 보세요."}
        </div>
      )}
      {!state.loading && data?.supported && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginTop: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>필수 조건 — 모두 맞아야 이 세번</div>
            <ol data-testid="target-conditions-required" style={{ listStyle: "none", margin: 0, padding: 0, borderTop: `1px solid ${LINE}` }}>
              {required.map((r, i) => {
                const m = r.kind === "legal_text" ? { icon: "§", color: MUTED, label: "원문" } : r.kind === "consistency" ? { icon: "?", color: MUTED, label: "정합성 확인" } : mark(r.state);
                return (
                  <li key={`${r.level}-${i}`} data-state={r.state} data-kind={r.kind || "condition"} style={{ display: "grid", gridTemplateColumns: "20px 64px minmax(0,1fr) auto", columnGap: 8, padding: "7px 0", borderBottom: `1px solid ${LINE}`, fontSize: 12.5, alignItems: "baseline" }}>
                    <span aria-label={m.label} style={{ color: m.color, fontWeight: 700 }}>{m.icon}</span>
                    <span style={{ color: MUTED, fontSize: 11 }}>{LEVEL_LABEL[r.level] || r.level}</span>
                    <span style={{ lineHeight: 1.45, overflowWrap: "anywhere", color: r.kind === "legal_text" ? MUTED : "inherit" }}>{textOf(r)}</span>
                    <span style={{ fontSize: 11.5, color: r.state === "true" ? BLUE : MUTED, whiteSpace: "nowrap" }}>{r.kind === "legal_text" ? "원문 문구" : r.state === "invalid" ? `${answerText(r)} · 값 범위 밖` : answerText(r) || m.label}</span>
                  </li>
                );
              })}
            </ol>
            {data.domestic === "no_table" && String(code).length === 10 && <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>이 소호에는 국내 결정표가 없어 10자리 조건은 확인하지 못했습니다.</div>}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>배제 조건 — 하나라도 해당하면 다른 곳으로</div>
            {exclusions.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>트리에 적힌 배제 조건이 없습니다.</div>}
            <ul data-testid="target-conditions-exclusions" style={{ listStyle: "none", margin: 0, padding: 0, borderTop: exclusions.length ? `1px solid ${LINE}` : 0 }}>
              {exclusions.map((x, i) => (
                <li key={`x-${i}`} data-state={x.state} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 16px minmax(0, 46%)", columnGap: 8, padding: "7px 0", borderBottom: `1px solid ${LINE}`, fontSize: 12.5, alignItems: "baseline" }}>
                  <span style={{ lineHeight: 1.45, overflowWrap: "anywhere", fontWeight: x.state === "true" ? 700 : 400 }}>{x.text}{x.state === "true" ? <span style={{ color: BLUE, fontSize: 11 }}> · 지금 답과 맞음</span> : null}</span>
                  <span aria-hidden="true" style={{ color: MUTED }}>→</span>
                  <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {(x.destinations || []).length ? x.destinations.map(destButton) : <span style={{ color: MUTED, fontSize: 12 }}>이 세번 밖</span>}
                    {x.more_destinations ? <span style={{ color: MUTED, fontSize: 11 }}>외 {x.more_destinations}곳</span> : null}
                  </span>
                </li>
              ))}
            </ul>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>도착 세번은 그곳의 다른 조건까지 맞아야 실제로 그 세번이 됩니다.</div>
          </div>
        </div>
      )}
      {!state.loading && data?.supported && (
        <div style={{ fontSize: 11, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
          조건은 관세율표 원문을 옮긴 결정트리에서 읽습니다. 수치는 원문 경계(이상·초과)를 그대로 씁니다. 답은 위 지도의 질문에서 하며, 모두 확인되기 전까지 결과는 조건부입니다.
          {data.other_consistency_checks ? ` 이 밖에 류 고유의 정합성 확인 ${data.other_consistency_checks}개가 있습니다.` : ""}
        </div>
      )}
    </section>
  );
}
